import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Calendar, Eye, BookOpen, ArrowRight, Tag } from "lucide-react";
import api from "../lib/api";
import Seo from "../components/Seo";
import { useI18n } from "../i18n/I18nContext";

export default function Blog() {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(params.get("q") || "");
  const activeCategory = params.get("category") || "";

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/blog/posts", { params: { q: search || undefined, category: activeCategory || undefined, limit: 30 } }),
      api.get("/blog/categories"),
    ]).then(([p, c]) => {
      setPosts(p.data.items || []);
      setCategories(c.data || []);
    }).finally(() => setLoading(false));
  }, [search, activeCategory]);

  const setCategory = (cat) => {
    const next = new URLSearchParams(params);
    if (cat) next.set("category", cat); else next.delete("category");
    setParams(next);
  };

  const featured = posts[0];
  const rest = posts.slice(1);

  return (
    <div data-testid="blog-page" className="bg-slate-50 min-h-screen">
      <Seo
        title="Blog & Car Guides"
        description="Expert advice, buying guides, rental tips and the latest news from Cambodia's car marketplace."
      />
      {/* Hero */}
      <section className="bg-cm-primary text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-xs font-medium tracking-wider uppercase">
            <BookOpen className="w-3.5 h-3.5" /> Car Market Insights
          </span>
          <h1 className="mt-5 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05] max-w-3xl">
            Stories, guides & news for <span className="text-cm-accent">Cambodian drivers.</span>
          </h1>
          <p className="mt-4 text-base sm:text-lg text-slate-300 max-w-2xl">
            Expert buying guides, rental tips, dealer spotlights and the latest market trends.
          </p>
          <form onSubmit={(e) => e.preventDefault()} className="mt-7 max-w-xl bg-white text-cm-primary rounded-2xl p-2 flex items-center gap-2">
            <Search className="w-5 h-5 text-slate-400 ml-2" />
            <input
              data-testid="blog-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search articles, guides, tips…"
              className="flex-1 bg-transparent outline-none text-base placeholder:text-slate-400 py-2"
            />
          </form>
        </div>
      </section>

      {/* Categories */}
      <div className="sticky top-16 z-30 glass border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex gap-2 overflow-x-auto no-scrollbar">
          <button onClick={() => setCategory("")} data-testid="blog-cat-all"
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${!activeCategory ? 'bg-cm-primary text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'}`}>
            All articles
          </button>
          {categories.map((c) => (
            <button key={c} onClick={() => setCategory(c)} data-testid={`blog-cat-${c}`}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${activeCategory === c ? 'bg-cm-primary text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'}`}>
              {c}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => <div key={i} className="aspect-[4/3] bg-slate-100 rounded-2xl animate-pulse" />)}
          </div>
        ) : posts.length === 0 ? (
          <div data-testid="blog-empty" className="text-center py-20 text-slate-500">No articles published yet.</div>
        ) : (
          <>
            {featured && (
              <Link to={`/blog/${featured.slug}`} data-testid={`blog-featured-${featured.slug}`}
                className="group block bg-white rounded-3xl border border-slate-200 overflow-hidden hover:shadow-xl transition-all mb-10">
                <div className="grid md:grid-cols-2">
                  <div className="aspect-[16/10] md:aspect-auto bg-slate-100 overflow-hidden">
                    {featured.cover_image && <img src={featured.cover_image} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                  </div>
                  <div className="p-7 md:p-10 flex flex-col justify-center">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-cm-accent">
                      <Tag className="w-3 h-3" /> {featured.category}
                    </div>
                    <h2 className="mt-3 font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl text-cm-primary leading-tight">{featured.title}</h2>
                    <p className="mt-3 text-slate-600 line-clamp-3">{featured.excerpt}</p>
                    <div className="mt-5 flex items-center gap-4 text-xs text-slate-500">
                      <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(featured.published_at || featured.created_at).toLocaleDateString()}</span>
                      <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {featured.views || 0}</span>
                    </div>
                    <span className="mt-5 inline-flex items-center gap-1.5 text-cm-accent font-semibold">Read article <ArrowRight className="w-4 h-4" /></span>
                  </div>
                </div>
              </Link>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 stagger">
              {rest.map((p) => (
                <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`blog-post-${p.slug}`}
                  className="group bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all">
                  <div className="aspect-[16/10] bg-slate-100 overflow-hidden">
                    {p.cover_image && <img src={p.cover_image} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-cm-accent">
                      <Tag className="w-3 h-3" /> {p.category}
                    </div>
                    <h3 className="mt-2 font-display font-bold text-lg text-cm-primary line-clamp-2">{p.title}</h3>
                    <p className="mt-2 text-sm text-slate-600 line-clamp-2">{p.excerpt}</p>
                    <div className="mt-4 flex items-center gap-3 text-xs text-slate-500">
                      <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(p.published_at || p.created_at).toLocaleDateString()}</span>
                      <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {p.views || 0}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
